// Standalone strain-reviews — modal HOST styling only.
//
// The popup INTERNALS (sticky bar, infographic, terpene cards, lineage
// orbital, etc.) are rendered by the real chatbot renderer in
// ../features/strain_info.js and styled by ITS OWN injected CSS
// (ensureStrainOverlayStyles). This file only provides the standalone
// shell: a dim backdrop + a centered phone-width frame that the chatbot's
// `.vb-strain-overlay` (position:absolute; inset:0) mounts INTO — so the
// popup looks pixel-identical to how it looks docked inside the chat widget.

import { STYLES_ID } from './config.js';

export const STYLES_CSS = `
.vbsr-backdrop {
    position: fixed; inset: 0;
    background: rgba(15, 23, 42, 0.55);
    z-index: 2147483645;
    animation: vbsr-fade 0.18s ease-out;
}
@keyframes vbsr-fade { from { opacity: 0; } to { opacity: 1; } }

/* The host is the bounded box the chatbot overlay fills. Sized to the same
   width the popup was designed for inside the chat widget (~400px) so the
   layout matches the in-chat experience exactly. */
.vbsr-host {
    position: fixed;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: min(420px, 100vw);
    height: min(86vh, 780px);
    background: #F5F5F7;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 30px 80px rgba(0,0,0,0.42);
    z-index: 2147483646;
    animation: vbsr-pop 0.22s ease-out;
}
@keyframes vbsr-pop {
    from { transform: translate(-50%, -48%) scale(0.98); opacity: 0.5; }
    to   { transform: translate(-50%, -50%) scale(1); opacity: 1; }
}

/* Floating close affordance in the top-right corner of the host. The
   popup also has its own "Back" button in the sticky bar; this is a
   redundant, always-visible escape for desktop users who expect an X. */
.vbsr-host-close {
    position: absolute;
    top: 10px; right: 12px;
    z-index: 2147483647;
    width: 30px; height: 30px;
    border-radius: 999px;
    border: 0;
    background: rgba(0,0,0,0.06);
    color: #1d1d1f;
    font-size: 17px; line-height: 1;
    cursor: pointer;
    display: flex; align-items: center; justify-content: center;
    transition: background 0.12s;
}
.vbsr-host-close:hover { background: rgba(0,0,0,0.12); }

@media (max-width: 480px) {
    .vbsr-host {
        width: 100vw; height: 100vh;
        border-radius: 0;
        top: 0; left: 0; transform: none;
    }
    @keyframes vbsr-pop {
        from { transform: translateY(10px); opacity: 0.4; }
        to   { transform: translateY(0); opacity: 1; }
    }
}

/* §7 — Mobile clipping fix (STANDALONE menu popup ONLY; ships in
   strain-reviews.js, never affects the in-chat chatbot popup). On real phones
   (~375px) the 420px popup is wider than the screen so wide rows + the
   fixed-size effects radar SVG clip horizontally. Keep the centered card, just
   stop the clipping and shrink the radar to fit. Mirrors BRAND_MOBILE_CSS in
   strain-menu-proxy/middleware.js. */
@media (max-width:480px){
  .vbsr-host{width:100vw;max-width:100vw;left:0;right:0;transform:translateY(-50%);border-radius:14px 14px 0 0;}
  .vbsr-backdrop{padding:0;}
  .vb-strain-overlay,.vb-pop--info2,.info2-body{overflow-x:hidden;max-width:100%;}
  .info2-radar-wrap,.info2-radar{max-width:100%;}
  .info2-radar svg,.info2-radar-wrap svg{width:100%;height:auto;max-width:100%;}
  .info2-stats-row,.info2-top-effect-stats,.info2-pop-meta{flex-wrap:wrap;}
  .info2-top-effect-stats span,.info2-pop-meta{white-space:normal;}
}

/* §6 — Branding footer (lead-gen) styling. STANDALONE menu popup ONLY (the
   footer node is appended only in strain-reviews/index.js, never in the shared
   strain_info.js renderer, so the in-chat paid chatbot popup never shows it).
   All screens. Look mirrors BRAND_MOBILE_CSS in strain-menu-proxy/middleware.js. */
.vbsr-brand-footer {
    display: flex; align-items: center; justify-content: space-between;
    gap: 8px; flex-wrap: wrap;
    padding: 12px 16px;
    border-top: 1px solid rgba(0,0,0,0.08);
    background: #fff;
    font: 500 12px/1.3 -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
}
.vbsr-brand-credit { color: #6e6e73; text-decoration: none; white-space: nowrap; }
.vbsr-brand-credit b { color: #3d7233; }
.vbsr-brand-cta {
    color: #fff; background: #3d7233;
    padding: 7px 12px; border-radius: 999px;
    text-decoration: none; font-weight: 600; white-space: nowrap;
}
.vbsr-brand-cta:hover { filter: brightness(1.08); }
`;

export function injectStyles() {
    if (typeof document === 'undefined') return;
    if (document.getElementById(STYLES_ID)) return;
    const style = document.createElement('style');
    style.id = STYLES_ID;
    style.textContent = STYLES_CSS;
    document.head.appendChild(style);
}
